<div class="min-h-screen bg-gray-900 py-8">
  <app-notification></app-notification>

  <div class="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
    <!-- Header -->
    <div class="flex items-center justify-between mb-8">
      <div class="flex items-center gap-4">
        <button
          (click)="cancel()"
          class="p-2 text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg transition-colors">
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <div>
          <h1 class="text-2xl font-bold text-white">
            {{ isNew ? 'Nieuw bericht' : 'Bericht bewerken' }}
          </h1>
          <p class="text-sm text-gray-400">
            {{ isNew ? 'Vul de gegevens in' : 'Bewerken in ' + getLanguageName(currentLocale) }}
          </p>
        </div>
      </div>
      <div class="flex items-center gap-3">
        <a *ngIf="previewUrl"
          [href]="previewUrl"
          target="_blank"
          rel="noopener noreferrer"
          class="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-300 hover:text-white border border-gray-600 hover:border-gray-500 rounded-lg transition-colors"
          title="Open preview op Vercel met domein-override (geen cache)">
          <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
          </svg>
          Bekijk preview
        </a>
        <button (click)="cancel()" class="px-4 py-2 text-gray-300 hover:text-white transition-colors">
          Annuleren
        </button>
        <button
          (click)="save()"
          [disabled]="isSaving || !(postForm.valid)"
          class="inline-flex items-center px-5 py-2 bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
          <svg *ngIf="isSaving" class="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <svg *ngIf="!isSaving" class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
          </svg>
          {{ isSaving ? 'Opslaan...' : 'Opslaan' }}
        </button>
      </div>
    </div>

    <!-- Loading overlay bij taalwisseling -->
    <div *ngIf="isLoading" class="flex justify-center items-center py-20">
      <div class="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
    </div>

    <!-- Form — direct zichtbaar, data vult zich progressief -->
    <form *ngIf="!isLoading && postForm" [formGroup]="postForm" class="space-y-6">
      <!-- Language tabs -->
      <div class="border-b border-gray-700">
        <nav class="flex gap-4">
          <button
            *ngFor="let lang of availableLanguages"
            type="button"
            (click)="setCurrentLocale(lang.code)"
            class="px-3 py-2 text-sm font-medium border-b-2 -mb-px flex items-center gap-2"
            [class.text-indigo-400]="currentLocale === lang.code"
            [class.border-indigo-400]="currentLocale === lang.code"
            [class.text-gray-400]="currentLocale !== lang.code"
            [class.border-transparent]="currentLocale !== lang.code">
            {{ getLanguageName(lang.code) }}
            <!-- Indicator: taalversie bestaat -->
            <span
              *ngIf="hasLanguageVersion(lang.code)"
              class="w-2 h-2 rounded-full bg-green-400"
              title="Vertaling aanwezig">
            </span>
            <!-- Indicator: geen taalversie -->
            <span
              *ngIf="!hasLanguageVersion(lang.code) && !isNew"
              class="w-2 h-2 rounded-full bg-gray-600"
              title="Nog geen vertaling">
            </span>
          </button>
        </nav>
      </div>

      <!-- Title -->
      <div>
        <label class="block text-sm font-medium text-gray-300 mb-2">Titel</label>
        <input
          formControlName="title"
          (input)="onTitleChange($any($event.target).value)"
          class="w-full px-4 py-2.5 bg-gray-700 border border-gray-600 rounded-lg text-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-500"
          [placeholder]="'Titel in ' + getLanguageName(currentLocale)" />
      </div>

      <!-- Content -->
      <div>
        <label class="block text-sm font-medium text-gray-300 mb-2">Inhoud</label>
        <editor formControlName="content" [init]="editorConfig"></editor>
      </div>

      <!-- HTML blok (optioneel) - onderaan de post, met script-executie -->
      <details class="bg-gray-800/50 rounded-lg border border-gray-700 group">
        <summary class="cursor-pointer select-none px-4 py-3 flex items-center justify-between text-sm font-medium text-gray-300 hover:text-white">
          <span class="flex items-center gap-2">
            <svg class="w-4 h-4 text-indigo-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
              <path stroke-linecap="round" stroke-linejoin="round" d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
            </svg>
            HTML blok (optioneel)
            <span *ngIf="postForm.get('htmlBlock')?.value" class="text-xs text-indigo-400 font-normal">- ingevuld</span>
          </span>
          <svg class="w-4 h-4 transition-transform group-open:rotate-180 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M19 9l-7 7-7-7" />
          </svg>
        </summary>
        <div class="px-4 pb-4">
          <p class="text-xs text-gray-500 mb-2 leading-relaxed">
            Ruw HTML / CSS / JavaScript dat NA de post-content wordt weergegeven op de live site. Handig voor bv. een aanmeldformulier, embed of andere widget.
            <code class="text-indigo-300">&lt;style&gt;</code> en <code class="text-indigo-300">&lt;script&gt;</code> blokken worden op de live site uitgevoerd.
          </p>
          <textarea
            formControlName="htmlBlock"
            rows="12"
            spellcheck="false"
            autocorrect="off"
            autocapitalize="off"
            placeholder="&lt;div&gt;...&lt;/div&gt;&#10;&lt;style&gt;...&lt;/style&gt;&#10;&lt;script&gt;...&lt;/script&gt;"
            class="w-full font-mono text-xs bg-gray-900 border border-gray-700 rounded-md px-3 py-2 text-gray-100 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500">
          </textarea>
        </div>
      </details>

      <!-- Domeinen: bij welke domeinen hoort dit bericht -->
      <div class="bg-gray-800/50 rounded-lg p-4 border border-gray-700">
        <label class="block text-sm font-medium text-gray-300 mb-2">Bij welke domeinen hoort dit bericht?</label>
        <p class="text-xs text-gray-500 mb-3">Selecteer één of meerdere domeinen waarop dit bericht getoond moet worden. Minimaal één verplicht.</p>
        <div class="flex flex-wrap gap-4">
          <label *ngFor="let d of domains" class="inline-flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              [checked]="isDomainSelected(d.name)"
              (change)="toggleDomain(d.name)"
              class="w-4 h-4 rounded bg-gray-700 border-gray-600 text-indigo-600 focus:ring-indigo-500" />
            <span class="text-sm text-gray-300">{{ d.name }}</span>
          </label>
        </div>
      </div>

      <!-- Featured image, status, category, slug -->
      <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label class="block text-sm font-medium text-gray-300 mb-2">Uitgelichte afbeelding</label>
          <div class="h-32 w-full bg-gray-700 rounded-lg overflow-hidden mb-2">
            <img
              *ngIf="postForm.get('featuredImage')?.value"
              [src]="postForm.get('featuredImage')?.value"
              class="h-full w-full object-cover"
              alt="Uitgelicht" />
            <div *ngIf="!postForm.get('featuredImage')?.value" class="h-full flex items-center justify-center text-gray-500">
              <svg class="h-12 w-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
          </div>
          <div class="flex flex-wrap gap-3">
            <label class="inline-flex items-center px-4 py-2 bg-gray-700 hover:bg-gray-600 text-gray-200 text-sm font-medium rounded-lg transition-colors cursor-pointer border border-gray-600">
              <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
              </svg>
              Uploaden
              <input type="file" accept="image/*" (change)="uploadFeaturedImage($event)" class="hidden">
            </label>
            <button
              type="button"
              (click)="openMediaSelector('featured')"
              class="inline-flex items-center px-4 py-2 bg-gray-700 hover:bg-gray-600 text-gray-200 text-sm font-medium rounded-lg transition-colors border border-gray-600">
              <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              Uit mediabibliotheek
            </button>
          </div>
        </div>
        <div class="space-y-4">
          <div>
            <label class="block text-sm font-medium text-gray-300 mb-2">Status</label>
            <select formControlName="status" class="w-full px-4 py-2.5 bg-gray-700 border border-gray-600 rounded-lg text-gray-200">
              <option value="draft">Concept</option>
              <option value="published">Gepubliceerd</option>
            </select>
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-300 mb-2">Categorieën</label>
            <div class="flex flex-wrap gap-3 py-2">
              <label *ngFor="let c of categories" class="inline-flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  [checked]="isCategorySelected(c.id)"
                  (change)="toggleCategory(c.id)"
                  class="w-4 h-4 rounded bg-gray-700 border-gray-600 text-indigo-600 focus:ring-indigo-500" />
                <span class="text-sm text-gray-300">{{ c.name }}</span>
              </label>
            </div>
            <p *ngIf="categories.length === 0" class="text-sm text-gray-500">Geen categorieën. Voeg er een toe hieronder.</p>
            <div class="mt-2">
              <button
                *ngIf="!showNewCategoryInput"
                type="button"
                (click)="showNewCategoryInput = true"
                class="text-sm text-indigo-400 hover:text-indigo-300">
                + Nieuwe categorie toevoegen
              </button>
              <div *ngIf="showNewCategoryInput" class="flex flex-wrap gap-2 items-center mt-1">
                <input
                  type="text"
                  [(ngModel)]="newCategoryName"
                  [ngModelOptions]="{standalone: true}"
                  (keydown.enter)="addCategory(); $event.preventDefault()"
                  placeholder="Naam categorie"
                  class="flex-1 min-w-[120px] px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-gray-200 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent" />
                <button type="button" (click)="addCategory()" class="px-3 py-2 bg-indigo-600 text-white text-sm rounded-lg hover:bg-indigo-500">
                  Toevoegen
                </button>
                <button type="button" (click)="cancelNewCategory()" class="px-3 py-2 bg-gray-600 text-gray-200 text-sm rounded-lg hover:bg-gray-500">
                  Annuleren
                </button>
              </div>
            </div>
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-300 mb-2">URL-slug <span class="text-red-400">*</span></label>
            <input formControlName="slug" class="w-full px-4 py-2.5 bg-gray-700 border border-gray-600 rounded-lg text-gray-200" placeholder="url-vriendelijke-slug" />
            <p *ngIf="postForm.get('slug')?.value" class="mt-1 text-xs text-gray-500 font-mono">/{{ postUrlPrefix }}/{{ postForm.get('slug')?.value }}</p>
          </div>
        </div>
      </div>

      <!-- SEO -->
      <div class="bg-gray-800 rounded-xl border border-gray-700 p-6">
        <h3 class="text-lg font-medium text-gray-200 mb-1">SEO</h3>
        <p class="text-sm text-gray-400 mb-4">Meta title, beschrijving en trefwoorden voor {{ getLanguageName(currentLocale) }}.</p>
        <div class="space-y-4">
          <div>
            <input formControlName="metaTitle" class="w-full px-4 py-2.5 bg-gray-700 border border-gray-600 rounded-lg text-gray-200" [placeholder]="'Meta titel (' + getLanguageName(currentLocale) + ')'" />
          </div>
          <div>
            <textarea formControlName="metaDescription" rows="2" class="w-full px-4 py-2.5 bg-gray-700 border border-gray-600 rounded-lg text-gray-200" [placeholder]="'Meta beschrijving (' + getLanguageName(currentLocale) + ')'"></textarea>
          </div>
          <div>
            <label class="block text-sm text-gray-400 mb-2">Trefwoorden (Enter of komma)</label>
            <div class="flex flex-wrap gap-2 p-2 bg-gray-700 rounded-lg">
              <span *ngFor="let kw of postForm.get('metaKeywords')?.value" class="inline-flex items-center px-2 py-1 rounded bg-gray-600 text-gray-200 text-sm">
                {{ kw }}
                <button type="button" (click)="removeKeyword(kw)" class="ml-1 text-gray-400 hover:text-white">&times;</button>
              </span>
              <input type="text" class="flex-1 min-w-[120px] bg-transparent border-0 text-gray-200 focus:ring-0" placeholder="Toevoegen..." (keydown)="onKeywordInput($event)" />
            </div>
          </div>
        </div>
      </div>

      <!-- Open Graph -->
      <div class="bg-gray-800 rounded-xl border border-gray-700 p-6">
        <h3 class="text-lg font-medium text-gray-200 mb-1">Open Graph (sociale media)</h3>
        <p class="text-sm text-gray-400 mb-4">Titel, beschrijving en afbeelding voor delingen.</p>
        <div class="space-y-4">
          <div>
            <input formControlName="ogTitle" class="w-full px-4 py-2.5 bg-gray-700 border border-gray-600 rounded-lg text-gray-200" [placeholder]="'OG titel (' + getLanguageName(currentLocale) + ')'" />
          </div>
          <div>
            <textarea formControlName="ogDescription" rows="2" class="w-full px-4 py-2.5 bg-gray-700 border border-gray-600 rounded-lg text-gray-200" [placeholder]="'OG beschrijving (' + getLanguageName(currentLocale) + ')'"></textarea>
          </div>
          <div>
            <label class="block text-sm text-gray-400 mb-2">OG-afbeelding (bv. 1200×630)</label>
            <div class="flex gap-4">
              <div class="w-40 h-28 bg-gray-700 rounded-lg overflow-hidden flex-shrink-0">
                <img *ngIf="postForm.get('ogImage')?.value" [src]="postForm.get('ogImage')?.value" class="w-full h-full object-cover" alt="OG" />
                <div *ngIf="!postForm.get('ogImage')?.value" class="w-full h-full flex items-center justify-center text-gray-500">
                  <svg class="h-10 w-10" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14" /></svg>
                </div>
              </div>
              <div class="flex flex-wrap gap-3">
                <label class="inline-flex items-center px-4 py-2 bg-gray-700 hover:bg-gray-600 text-gray-200 text-sm font-medium rounded-lg transition-colors cursor-pointer border border-gray-600">
                  <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                  </svg>
                  Uploaden
                  <input type="file" accept="image/*" (change)="uploadOgImage($event)" class="hidden">
                </label>
                <button
                  type="button"
                  (click)="openMediaSelector('og')"
                  class="inline-flex items-center px-4 py-2 bg-gray-700 hover:bg-gray-600 text-gray-200 text-sm font-medium rounded-lg transition-colors border border-gray-600">
                  <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  Uit mediabibliotheek
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </form>
  </div>
</div>

<!-- Media Selector Modal -->
<app-media-selector-modal
  [show]="showMediaSelector"
  (onSelect)="onMediaSelected($event)"
  (onClose)="closeMediaSelector()">
</app-media-selector-modal>
